import { Component, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';

import {
  ProcessErrorCodeAdapterService,
  ProcessErrorCodeFn,
} from 'src/app/components/scrolling-list/services/process-error-code-adapter.service';
import { ApiUtilsService } from 'src/app/api/services/api-utils.service';
import { UserApiService } from 'src/app/api/services/user-api.service';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { UserStateService } from 'src/app/auth/services/user-state.service';

import { ActionsMenu } from 'src/app/components/common-actions/common-actions.component';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { BaseEditableComponent } from 'src/app/shared/components/base-editable/base-editable.component';
import { ValidationService } from 'src/app/shared/services/validation.service';
import { UserFieldsComponent } from 'src/app/settings/components/user-fields/user-fields.component';
import { StateService } from '@uirouter/core';

@Component({
  selector: 'user-add',
  templateUrl: './user-add.component.html',
  styleUrls: ['./user-add.component.scss'],
  standalone: false,
})
export class UserAddComponent extends BaseEditableComponent<any> {
  user: any = {
    username: '',
    firstName: '',
    lastName: '',
    email: '',
    telephone: '',
    companyRole: '',
    roles: [],
  };
  loading = false;
  formError = null;
  apiError = null;
  isUserAdmin = this.userStateService.isUserAdmin();

  @ViewChild('userForm')
  public userForm: NgForm;

  @ViewChild(UserFieldsComponent) userFieldsComponent: UserFieldsComponent;

  EMAIL_REGEX = ValidationService.EMAIL_REGEX;

  actions: ActionsMenu = {
    saveAction: {
      id: 'saveButton',
      label: 'Save',
      action: () => this.save(),
      requireRole: 'ua sa',
      disabled: false,
    },
    cancelAction: {
      id: 'cancelButton',
      label: 'Cancel',
      action: () => this.stateService.go('apps.settings.user-list'),
    },
  };

  private processErrorCode: ProcessErrorCodeFn;

  constructor(
    protected stateService: StateService,
    private userStateService: UserStateService,
    private companyStateService: CompanyStateService,
    private userApiService: UserApiService,
    private tracker: TrackerService,
    processErrorCodeAdapter: ProcessErrorCodeAdapterService,
    private apiUtils: ApiUtilsService,
  ) {
    super();
    this.processErrorCode = processErrorCodeAdapter.createCallable();
    this.oldObject = this.cloneDeep(this.user);
  }

  save() {
    // Check if the form is valid
    if (this.userForm.invalid) {
      // Mark all fields as touched to trigger validation messages
      Object.keys(this.userForm.controls).forEach((key) => {
        const control = this.userForm.controls[key];
        control.markAsTouched();
      });
      return;
    }

    // Validate required fields from userFieldsComponent
    const formData = this.userFieldsComponent.editUser;
    if (
      !formData.username ||
      !formData.firstName ||
      !formData.lastName ||
      !formData.email
    ) {
      this.formError = 'Please fill in all required fields.';
      return;
    }

    // Validate email format
    if (
      !ValidationService.EMAIL_REGEX.test(formData.username) ||
      !ValidationService.EMAIL_REGEX.test(formData.email)
    ) {
      this.formError = 'Please enter valid email addresses.';
      return;
    }

    let companyId = this.companyStateService.getSelectedCompanyId();
    this.loading = true;
    this.formError = null;
    this.apiError = null;

    // Ensure we have all required fields
    const userData = {
      username: formData.username,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      telephone: formData.telephone || '',
      companyRole: formData.companyRole,
      roles: formData.roles || [],
    };

    this.userApiService
      .add(companyId, userData.username, userData)
      .then(() => {
        this.tracker.userEvent(
          'User Created',
          this.userStateService.getUsername(),
          false,
          {
            invitedEmail: userData.username,
          },
        );
        this.oldObject = this.cloneDeep(userData);
        this.stateService.go('apps.user.details', {
          username: userData.username,
        });
      })
      .catch((e) => {
        let error = this.apiUtils.getError(e);
        this.formError = 'User could not be added.';
        this.apiError = this.processErrorCode(e);

        if (error.code === 409) {
          this.apiError = `A User with the Username '${userData.username}' already belongs
            to another Company. To add them to this Company, they must sign in
            and delete themselves from their current Company by opening their
            User Settings and clicking the Delete button.`;
        }
      })
      .finally(() => {
        this.loading = false;
      });
  }

  canDeactivate(): boolean {
    return this.isDeepEqual(this.oldObject, this.userFieldsComponent.editUser);
  }
}
